import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtErr } from "../lib/api";
import { mApi } from "../lib/memoria";
import { isNativeAndroid } from "../lib/billing";
import { toast } from "sonner";
import { X, MoonStar, Coins, ArrowLeft, Loader2, ExternalLink } from "lucide-react";
import { CardsIcon } from "./CardsIcon";
import SmazzoStesa from "../pages/SmazzoStesa";
import { RunePicker } from "./RunePicker";
import { MethodHeart } from "./MethodHeart";

const ICHING_CATEGORIES = [
  { id: "amore", label: "Amore" },
  { id: "lavoro", label: "Lavoro e finanze" },
  { id: "generale", label: "Consigli generali" },
];

const TAROCCHI_SUBSETS = [
  { id: "maggiore", label: "Soli Arcani Maggiori", sub: "22 carte" },
  { id: "minore", label: "Soli Arcani Minori", sub: "56 carte" },
  { id: "completo", label: "Mazzo completo", sub: "78 carte" },
];

const HEART_BY_DECK = { "vera-sibilla": "lettura" };

async function streamSSE(url, onEvent) {
  const res = await fetch(url, { method: "POST", credentials: "include" });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(typeof d.detail === "string" ? d.detail : "La lettura non è disponibile ora. Riprova tra poco.");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.trim();
      if (!line.startsWith("data: ")) continue;
      const ev = JSON.parse(line.slice(6));
      if (ev.type === "error") throw new Error(ev.content);
      onEvent(ev);
    }
  }
}

export default function ChatReading({ topicId, onDone, onClose }) {
  const [step, setStep] = useState("method"); // method|assign|spread|question|confirm|pick|tosses|reading
  const [method, setMethod] = useState(null);
  const profile = null; // la chat è sempre riferita all'utente stesso: niente scelta "Io/Altri"
  const [spread, setSpread] = useState(null);
  const [question, setQuestion] = useState("");
  const [ent, setEnt] = useState(null);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [readingId, setReadingId] = useState(null);
  const [streamText, setStreamText] = useState("");
  // geomanzia
  const [marks, setMarks] = useState([]);
  const squareRef = useRef(null);
  // i ching
  const [faceTwo, setFaceTwo] = useState(null);
  const [category, setCategory] = useState("");
  const [coinVals, setCoinVals] = useState([null, null, null]);
  const [throwsList, setThrowsList] = useState([]);
  const [subset, setSubset] = useState(null); // tarocchi: maggiore|minore|completo
  const [catalogo, setCatalogo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/metodi/catalogo").then((r) => setCatalogo(r.data)).catch(() => setCatalogo({ guided: [], dedicated: [] }));
  }, []);

  const guidedById = Object.fromEntries((catalogo?.guided || []).map((g) => [g.id, g]));

  useEffect(() => {
    if (step === "confirm") {
      api.get("/entitlements").then((r) => setEnt(r.data)).catch(() => setEnt(null));
    }
  }, [step]);

  const start = async () => {
    setBusy(true);
    try {
      if (method === "iching") {
        const { data } = await api.post("/iching/sessions", { category, question: question.trim(), face_two: faceTwo, private: true });
        setSession(data);
        setStep("tosses");
      } else if (method === "geomancy") {
        const { data } = await api.post("/geomancy/sessions", { question, profile_id: profile, private: true });
        setSession(data);
        setStep("pick");
      } else if (method === "rune") {
        const { data } = await api.post("/draw-sessions", { deck: method, spread, question, profile_id: profile, private: true });
        setSession(data);
        setStep("pick");
      } else {
        // Mazzi a carte: stesso rito delle sezioni dedicate (caos, mischia, smazzo, taglio), niente scelta manuale
        setStep("smazzo");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "quota_esaurita") {
        toast.error(d.message || "Consultazioni esaurite per oggi.");
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const interpretDraw = async () => {
    setStep("reading");
    setStreamText("");
    try {
      await streamSSE(`${API}/draw-sessions/${session.id}/interpret`, (ev) => {
        if (ev.type === "meta") setReadingId(ev.reading_id);
        if (ev.type === "delta") setStreamText((t) => t + ev.content);
      });
      const { data } = await mApi.post("/memoria/reading-done", { topic_id: topicId, source: "draw", source_id: readingIdRef.current });
      onDone(data);
    } catch (e) {
      toast.error(e.message || "Interpretazione non disponibile.");
      setStep("confirm");
    }
  };

  const handleSmazzoDone = async (rid) => {
    try {
      const { data } = await mApi.post("/memoria/reading-done", { topic_id: topicId, source: "draw", source_id: rid });
      onDone(data);
    } catch {
      toast.error("La lettura è pronta, ma non sono riuscita ad aprirla qui. Chiudi e riprova dalla conversazione.");
      onClose();
    }
  };

  const readingIdRef = useRef(null);
  useEffect(() => { readingIdRef.current = readingId; }, [readingId]);

  const interpretGeomancy = async (sid) => {
    setStep("reading");
    setStreamText("");
    try {
      await streamSSE(`${API}/geomancy/sessions/${sid}/interpret`, (ev) => {
        if (ev.type === "delta") setStreamText((t) => t + ev.content);
      });
      const { data } = await mApi.post("/memoria/reading-done", { topic_id: topicId, source: "geomancy", source_id: sid });
      onDone(data);
    } catch (e) {
      toast.error(e.message || "Interpretazione non disponibile.");
      setStep("confirm");
    }
  };

  const interpretIching = async () => {
    setStep("reading");
    setStreamText("");
    try {
      await streamSSE(`${API}/iching/sessions/${session.id}/interpret`, (ev) => {
        if (ev.type === "delta") setStreamText((t) => t + ev.content);
      });
      const { data } = await mApi.post("/memoria/reading-done", { topic_id: topicId, source: "iching", source_id: session.id });
      onDone(data);
    } catch (e) {
      toast.error(e.message || "Interpretazione non disponibile.");
      setStep("confirm");
    }
  };

  const confirmThrow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/iching/sessions/${session.id}/throw`, { values: coinVals });
      setThrowsList((t) => [...t, data.total]);
      setCoinVals([null, null, null]);
      if (data.done) {
        await interpretIching();
      }
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const finishRow = async () => {
    if (marks.length <= 8 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/geomancy/sessions/${session.id}/rows`, { clicks: marks.length });
      setSession(data);
      setMarks([]);
      if (data.status === "completed") {
        await interpretGeomancy(data.id);
      }
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSquareClick = (e) => {
    if (!session || session.status !== "active") return;
    const rect = squareRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMarks((m) => [...m, { x, y }]);
  };

  const quotaLine = () => {
    if (!ent) return "Verifica disponibilità…";
    if (ent.plan === "admin") return "Account amministratore: consultazioni illimitate.";
    const n = method === "stesa26" ? 2 : 1;
    if (ent.consults_left_today === null) return "Consultazioni illimitate.";
    if (ent.consults_left_today >= n) return `Questa lettura userà ${n} consultazion${n > 1 ? "i" : "e"}: oggi te ne restano ${ent.consults_left_today}.`;
    if (ent.consults_left_today + ent.credits >= n) return `Questa lettura richiede ${n} consultazioni: oggi te ne ${ent.consults_left_today === 1 ? "resta 1" : `restano ${ent.consults_left_today}`} e userà anche ${n - ent.consults_left_today} dei tuoi ${ent.credits} crediti.`;
    return isNativeAndroid()
      ? "Non hai consultazioni disponibili: acquista crediti o attiva l'abbonamento dalla pagina Piani."
      : "Non hai consultazioni disponibili: domani avrai una nuova consultazione gratuita, oppure attiva l'abbonamento dall'app Android di Arcana.";
  };

  const canStart = ent && (ent.plan === "admin" || ent.consults_left_today === null
    || ent.consults_left_today + ent.credits >= (method === "stesa26" ? 2 : 1));

  return (
    <div className="max-w-2xl w-full mx-auto px-4 py-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-xl text-amber-100 flex items-center gap-2">
          <CardsIcon className="w-4 h-4 text-amber-300" /> Nuova lettura
        </h3>
        <button onClick={onClose} data-testid="mr-close" className="p-1.5 text-purple-300/70 hover:text-purple-100">
          <X className="w-5 h-5" />
        </button>
      </div>

      {step === "method" && (
        <div data-testid="mr-step-method">
          <p className="text-xs text-purple-200/70 mb-3">Scegli il metodo di lettura:</p>
          {!catalogo ? (
            <p className="text-xs text-purple-300/50 py-4 text-center" data-testid="mr-methods-loading">Preparo i metodi…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {catalogo.guided.map((m) => (
                  <div key={m.id} className="relative">
                    <button
                      data-testid={`mr-method-${m.id}`}
                      onClick={() => {
                        setMethod(m.id);
                        setSpread(null);
                        setSubset(null);
                        if (m.id === "iching") setStep("assign");
                        else if (m.id === "geomancy") setStep("question");
                        else if (m.id === "tarocchi") setStep("subset");
                        else if (m.spreads.length === 1) { setSpread(m.spreads[0].id); setStep("question"); }
                        else setStep("spread");
                      }}
                      className="w-full rounded-2xl border border-[#4a2175] bg-[#180a26] px-4 py-5 text-center hover:border-amber-500/60 transition-colors"
                    >
                      <p className="font-display text-base text-amber-100">{m.label}</p>
                      <p className="text-purple-200/60 text-[11px] mt-1">{m.desc}</p>
                    </button>
                    <MethodHeart metodo={HEART_BY_DECK[m.id] || m.id} testid={`mr-heart-${m.id}`} />
                  </div>
                ))}
              </div>
              {catalogo.dedicated.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#4a2175]/50" data-testid="mr-dedicated-section">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/70 mb-2 text-center">Sistemi dedicati</p>
                  <div className="grid grid-cols-2 gap-3">
                    {catalogo.dedicated.map((m) => (
                      <div key={m.id} className="relative">
                        <button
                          data-testid={`mr-method-${m.id}`}
                          onClick={() => {
                            if (m.id === "stesa26") {
                              // La Stesa 26 resta nel Lucchetto: stesso rito incorporato, lettura privata
                              setMethod("stesa26");
                              setSpread("stesa26");
                              setSubset(null);
                              setStep("question");
                            } else {
                              navigate(m.route);
                            }
                          }}
                          className="w-full rounded-2xl border border-[#4a2175] bg-[#180a26] px-4 py-5 text-center hover:border-amber-500/60 transition-colors"
                        >
                          <p className="font-display text-base text-amber-100">{m.label}</p>
                          <p className="text-purple-200/60 text-[11px] mt-1">{m.desc}</p>
                          <p className="text-[10px] text-amber-300/70 mt-2 inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> {m.id === "stesa26" ? "Falla qui, nel Lucchetto" : "Apri il suo rito"}
                          </p>
                        </button>
                        <MethodHeart metodo={m.id} testid={`mr-heart-${m.id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {step === "subset" && (
        <div data-testid="mr-step-subset">
          <p className="text-xs text-purple-200/70 mb-3">Scegli il mazzo dei Tarocchi:</p>
          <div className="grid grid-cols-1 gap-3">
            {TAROCCHI_SUBSETS.map((s) => (
              <button
                key={s.id}
                data-testid={`mr-subset-${s.id}`}
                onClick={() => {
                  setSubset(s.id);
                  if (s.id !== "maggiore") {
                    setSpread("tre");
                    setStep("question");
                  } else {
                    setSpread(null);
                    setStep("spread");
                  }
                }}
                className="rounded-2xl border border-[#4a2175] bg-[#180a26] px-4 py-4 text-center hover:border-amber-500/60 transition-colors"
              >
                <p className="font-display text-sm text-amber-100">{s.label}</p>
                <p className="text-purple-200/60 text-[11px] mt-1">{s.sub}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === "assign" && (
        <div data-testid="mr-step-assign">
          <p className="text-xs text-purple-200/70 mb-3 leading-relaxed">Ogni moneta ha due facce: stabilisci quale vale 2 e quale vale 3. La scelta varrà per tutti e sei i lanci della consultazione.</p>
          <p className="font-display text-amber-100 text-sm text-center">Quale faccia vale 2?</p>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {["Testa", "Croce"].map((f) => (
              <button key={f} data-testid={`mr-iching-face2-${f.toLowerCase()}`} onClick={() => setFaceTwo(f)}
                className={`py-2.5 rounded-full border text-sm transition-colors ${faceTwo === f ? "bg-[#d4a853] text-[#1a0b2e] font-bold border-[#d4a853]" : "border-amber-500/40 text-amber-200 hover:bg-amber-500/10"}`}>
                {f}
              </button>
            ))}
          </div>
          {faceTwo && (
            <p className="text-center text-xs text-purple-300/60 mt-3" data-testid="mr-iching-assignment">Quindi {faceTwo === "Testa" ? "Croce" : "Testa"} = 3</p>
          )}
          <div className="flex justify-end mt-4">
            <button data-testid="mr-assign-next" disabled={!faceTwo} onClick={() => setStep("question")} className="gold-btn px-6 py-2 text-xs disabled:opacity-40">Continua</button>
          </div>
        </div>
      )}

      {step === "spread" && guidedById[method] && (
        <div data-testid="mr-step-spread">
          <p className="text-xs text-purple-200/70 mb-3">Scegli la stesa:</p>
          <div className="grid grid-cols-2 gap-3">
            {guidedById[method].spreads
              .filter((s) => method !== "tarocchi" || subset !== "minore" || !["due", "siono"].includes(s.id))
              .map((s) => {
                const locked = method === "tarocchi" && subset !== "maggiore" && ["due", "siono"].includes(s.id);
                return (
                  <button
                    key={s.id}
                    data-testid={`mr-spread-${s.id}`}
                    onClick={() => { if (!locked) { if (s.id === "stesa26") setMethod("stesa26"); setSpread(s.id); setStep("question"); } }}
                    disabled={locked}
                    className={`rounded-2xl border px-4 py-4 text-center transition-colors ${locked ? "border-[#4a2175]/50 bg-[#180a26]/40 opacity-45 cursor-not-allowed" : "border-[#4a2175] bg-[#180a26] hover:border-amber-500/60"}`}
                  >
                    <p className="font-display text-sm text-amber-100">{s.label}</p>
                    <p className="text-purple-200/60 text-[11px] mt-1">{s.sub}</p>
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {step === "question" && (
        <div data-testid="mr-step-question">
          {method === "iching" && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="mr-iching-categories">
              {ICHING_CATEGORIES.map((c) => (
                <button key={c.id} data-testid={`mr-iching-cat-${c.id}`} onClick={() => setCategory(c.id)}
                  className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${category === c.id ? "bg-[#d4a853] text-[#1a0b2e] font-bold border-[#d4a853]" : "border-amber-500/40 text-amber-200 hover:bg-amber-500/10"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-purple-200/70 mb-3">Poni la tua domanda con serenità:</p>
          <textarea
            data-testid="mr-question-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={600}
            rows={3}
            placeholder="Cosa vuoi chiedere alle carte?"
            className="input-dark w-full resize-none"
          />
          <div className="flex justify-end mt-3">
            <button
              data-testid="mr-question-next"
              disabled={question.trim().length < 5 || (method === "iching" && !category)}
              onClick={() => setStep("confirm")}
              className="gold-btn px-6 py-2 text-xs disabled:opacity-40"
            >
              Continua
            </button>
          </div>
        </div>
      )}

      {step === "confirm" && (
        <div data-testid="mr-step-confirm" className="space-y-4">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 flex items-start gap-2.5">
            <Coins className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-100/90 leading-relaxed" data-testid="mr-quota-line">{quotaLine()}</p>
          </div>
          <p className="text-[11px] text-purple-300/50 leading-relaxed">
            La lettura userà il sistema di estrazione di Arcana: le carte saranno mescolate e scelte da te, poi interpretate. Le regole e i crediti sono gli stessi delle sezioni dedicate.
          </p>
          <button
            data-testid="mr-start-reading-btn"
            onClick={start}
            disabled={busy || !canStart}
            className="gold-btn w-full py-3 text-sm disabled:opacity-40"
          >
            {busy ? "Preparazione…" : "Conferma e inizia la lettura"}
          </button>
        </div>
      )}

      {step === "pick" && session && method === "rune" && (
        <div data-testid="mr-step-pick">
          <RunePicker sessionId={session.id} totalCards={session.total_cards} spread={session.count} onComplete={interpretDraw} />
        </div>
      )}

      {step === "smazzo" && (
        <div data-testid="mr-step-smazzo">
          <SmazzoStesa
            deck={method}
            embedded
            preset={{ spread, question, subset, sionoNumber: null }}
            onEmbeddedDone={handleSmazzoDone}
            onClose={onClose}
          />
        </div>
      )}

      {step === "pick" && session && method === "geomancy" && (
        <div data-testid="mr-step-geomancy">
          <p className="text-center text-xs text-amber-300/80 mb-3 font-cinzel tracking-[0.2em] uppercase" data-testid="mr-geo-counter">
            Riga {Math.min(session.rows.length + 1, 16)} di 16
          </p>
          <p className="text-center text-[11px] text-purple-300/60 mb-4">
            Tocca liberamente nel quadrato, seguendo il tuo istinto. Quando senti di aver finito, chiudi la riga.
          </p>
          <div
            ref={squareRef}
            onClick={handleSquareClick}
            data-testid="mr-geo-square"
            className="relative w-full max-w-xs mx-auto aspect-square rounded-2xl border border-[#4a2175] bg-[#150920] cursor-crosshair overflow-hidden"
          >
            {marks.map((m, i) => (
              <span key={i} className="absolute w-1.5 h-1.5 rounded-full bg-amber-300/80" style={{ left: `${m.x}%`, top: `${m.y}%` }} />
            ))}
          </div>
          <button
            data-testid="mr-geo-finish-row"
            onClick={finishRow}
            disabled={marks.length <= 8 || busy}
            className="gold-btn w-full mt-4 py-3 text-sm disabled:opacity-40"
          >
            Ho finito questa riga
          </button>
        </div>
      )}

      {step === "tosses" && session && (
        <div data-testid="mr-step-tosses">
          <p className="text-center text-[11px] text-amber-300/80 font-cinzel tracking-[0.2em] uppercase" data-testid="mr-iching-counter">
            Lancio {Math.min(throwsList.length + 1, 6)} di 6
          </p>
          <p className="text-center text-[11px] text-purple-300/60 mt-2 mb-4 leading-relaxed">
            Lancia fisicamente le tue tre monete e inserisci i valori usciti. La tua assegnazione: {session.faces["2"]} = 2 · {session.faces["3"]} = 3
          </p>
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between gap-3" data-testid={`mr-iching-coin-${i + 1}`}>
                <span className="text-amber-100 text-xs w-20">Moneta {i + 1}</span>
                <div className="flex gap-2">
                  {[2, 3].map((v) => (
                    <button key={v} data-testid={`mr-iching-coin-${i + 1}-val-${v}`}
                      onClick={() => setCoinVals((cv) => cv.map((x, j) => (j === i ? v : x)))}
                      className={`w-12 py-2 rounded-full border text-xs font-bold transition-colors ${coinVals[i] === v ? "bg-[#d4a853] text-[#1a0b2e] border-[#d4a853]" : "border-amber-500/40 text-amber-200 hover:bg-amber-500/10"}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button data-testid="mr-iching-confirm-throw" onClick={confirmThrow}
            disabled={busy || coinVals.some((v) => v === null)}
            className="gold-btn w-full mt-5 py-2.5 text-xs disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Conferma lancio"}
          </button>
          {throwsList.length > 0 && (
            <p className="text-center text-[11px] text-purple-300/50 mt-3" data-testid="mr-iching-progress">
              Lanci confermati: {throwsList.join(" · ")}
            </p>
          )}
        </div>
      )}

      {step === "reading" && (
        <div className="text-center py-10" data-testid="mr-step-reading">
          <MoonStar className="w-8 h-8 text-amber-400/70 mx-auto mb-4 animate-pulse" />
          <p className="text-purple-200/70 text-sm">Arcana sta interpretando ciò che è emerso…</p>
          {streamText && <p className="text-purple-300/40 text-[11px] mt-3">{Math.min(streamText.length, 999)} caratteri…</p>}
        </div>
      )}
    </div>
  );
}
